import type { StatModifier } from '../stats/StatSheet';
import type { GameEvents } from '../../core/EventBus';
import type { World } from '../World';
import { killFillMult } from '../run/progression';
import { SIM_HZ } from '../../core/GameLoop';

/**
 * The artifact pool (17), each a BASE BOON. Drops roll a QUALITY in
 * [0.80, 1.20] that scales boon modifier values, plus a PROC: blessed doubles
 * every boon modifier (hooks unchanged); cursed keeps the base boon and adds
 * one unscaled drawback from the shared CURSE_POOL. Procs are run-only —
 * banking stores base-form quality. Hooks are event-driven only (no per-tick
 * hook infra) and are NEVER quality-scaled.
 */

export type ArtifactProc = 'none' | 'cursed' | 'blessed';

export const BLESSED_BOON_MULT = 2;

export interface ArtifactHook<K extends keyof GameEvents = keyof GameEvents> {
  event: K;
  effect: (world: World, payload: GameEvents[K]) => void;
}

export interface ArtifactDef {
  id: string;
  name: string;
  desc: string;
  modifiers: StatModifier[];
  hooks?: ArtifactHook[];
}

/** Typed hook builder — narrows the payload to the named event's shape. */
function hook<K extends keyof GameEvents>(
  event: K,
  effect: (world: World, payload: GameEvents[K]) => void,
): ArtifactHook {
  return { event, effect } as ArtifactHook;
}

export const ARTIFACT_DEFS: ArtifactDef[] = [
  {
    id: 'whetstoneFang', name: 'Whetstone Fang',
    desc: '+18% damage',
    modifiers: [{ stat: 'damageMult', pct: 0.18 }],
  },
  {
    id: 'swiftAsh', name: 'Swift Ash',
    desc: '+12% move speed',
    modifiers: [{ stat: 'moveSpeed', pct: 0.12 }],
  },
  {
    id: 'hollowLens', name: 'Hollow Lens',
    desc: '+20% ability area',
    modifiers: [{ stat: 'areaMult', pct: 0.2 }],
  },
  {
    id: 'graveMagnet', name: 'Grave Magnet',
    desc: '+80 gem magnet range',
    modifiers: [{ stat: 'magnetRange', flat: 80 }],
  },
  {
    id: 'bloodLocket', name: 'Blood Locket',
    desc: '+1 max HP',
    modifiers: [{ stat: 'maxHp', flat: 1 }],
  },
  {
    id: 'quickSigil', name: 'Quick Sigil',
    desc: '-10% cooldowns',
    modifiers: [{ stat: 'cooldownMult', pct: -0.1 }],
  },
  {
    id: 'miserIdol', name: 'Miser Idol',
    desc: '+25% gem gain',
    modifiers: [{ stat: 'gemGainMult', pct: 0.25 }],
  },
  {
    id: 'thornCrown', name: 'Thorn Crown',
    desc: '+30% damage, retaliation burst when hit',
    modifiers: [{ stat: 'damageMult', pct: 0.3 }],
    hooks: [{
      event: 'playerHit',
      effect: (world) => {
        // Retaliation: damage everything near the player.
        const p = world.player;
        world.hash.query(p.x, p.y, 180, (idx) => {
          const e = world.enemies[idx];
          if (!e || !e.alive) return;
          const dx = e.x - p.x;
          const dy = e.y - p.y;
          if (dx * dx + dy * dy <= 170 * 170) world.damageEnemy(idx, 18);
        });
        world.events.emit('burstFired', { x: p.x, y: p.y, radius: 170 });
      },
    }],
  },
  {
    id: 'gluttonsMaw', name: "Glutton's Maw",
    desc: '+40% gem gain',
    modifiers: [{ stat: 'gemGainMult', pct: 0.4 }],
  },
  {
    id: 'saintsMarrow', name: "Saint's Marrow",
    desc: '+1 max HP and +10% damage',
    modifiers: [
      { stat: 'maxHp', flat: 1 },
      { stat: 'damageMult', pct: 0.1 },
    ],
  },
  {
    id: 'keeningQuill', name: 'Keening Quill',
    desc: '+25% projectile speed',
    modifiers: [{ stat: 'projSpeedMult', pct: 0.25 }],
  },
  {
    id: 'pallidHourglass', name: 'Pallid Hourglass',
    desc: '-8% cooldowns, +10% projectile speed',
    modifiers: [
      { stat: 'cooldownMult', pct: -0.08 },
      { stat: 'projSpeedMult', pct: 0.1 },
    ],
  },
  {
    id: 'mourningBell', name: 'Mourning Bell',
    desc: '+15% damage; slain elites toll a dying burst',
    modifiers: [{ stat: 'damageMult', pct: 0.15 }],
    hooks: [hook('enemyKilled', (world, e) => {
      if (!e.isElite) return;
      // Reentrancy is safe: the dying elite is alive=false before the emit
      // (same shape thornCrown exercises via damageEnemy chains).
      world.hash.query(e.x, e.y, 160, (idx) => {
        const en = world.enemies[idx];
        if (!en || !en.alive) return;
        const dx = en.x - e.x;
        const dy = en.y - e.y;
        if (dx * dx + dy * dy <= 150 * 150) world.damageEnemy(idx, 14);
      });
      world.events.emit('burstFired', { x: e.x, y: e.y, radius: 150 });
    })],
  },
  {
    id: 'ossuaryCoin', name: 'Ossuary Coin',
    desc: '+15% gem gain, +40 gem magnet range',
    modifiers: [
      { stat: 'gemGainMult', pct: 0.15 },
      { stat: 'magnetRange', flat: 40 },
    ],
  },
  {
    id: 'pilgrimsPhial', name: "Pilgrim's Phial",
    desc: '+1 max HP; mends 1 hp on each descent',
    modifiers: [{ stat: 'maxHp', flat: 1 }],
    // floorEntered fires on descend only (never on floor 1 or vault trips).
    hooks: [hook('floorEntered', (world) => {
      const healed = world.player.heal(1);
      if (healed > 0) world.events.emit('playerHealed', { amount: healed });
    })],
  },
  // ---- Ex-pool skills (M0.5): Knell and Threshold lost as build-slot
  // competitors but work as found bonuses — hook magnitudes fixed (never
  // quality-scaled, per the artifact contract); the boon carries quality.
  {
    id: 'passingBell', name: 'Passing Bell',
    desc: '+8% kill-bar fill; slain elites toll bonus fill',
    modifiers: [{ stat: 'killFillMult', pct: 0.08 }],
    hooks: [hook('enemyKilled', (world, e) => {
      if (!e.isElite) return;
      // The old Knell toll, through the one addBarFill funnel.
      world.runState.addBarFill(10 * killFillMult(world.floor));
      world.events.emit('knellToll', { x: e.x, y: e.y });
    })],
  },
  {
    id: 'wayfarersWard', name: "Wayfarer's Ward",
    desc: '+6% move speed; arrive below with a shock and a breath of shelter',
    modifiers: [{ stat: 'moveSpeed', pct: 0.06 }],
    // floorReady, NOT floorEntered: the shock must land on the NEW floor's
    // world (floorEntered fires before the swap). Direct enemy loop — the
    // spatial hash isn't built on arrival.
    hooks: [hook('floorReady', (world) => {
      const pl = world.player;
      pl.iframes = Math.max(pl.iframes, Math.round(1.5 * SIM_HZ));
      for (let i = 0; i < world.enemies.length; i++) {
        const e = world.enemies[i]!;
        if (!e.alive) continue;
        const dx = e.x - pl.x;
        const dy = e.y - pl.y;
        if (dx * dx + dy * dy <= (200 + e.radius) ** 2) world.damageEnemy(i, 20);
      }
      world.events.emit('thresholdShock', { x: pl.x, y: pl.y, radius: 200 });
    })],
  },
];

export const ARTIFACTS_BY_ID = new Map(ARTIFACT_DEFS.map((r) => [r.id, r]));
